/* eslint-disable */
import { observer } from "mobx-react-lite";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useStore } from "../../../../stores/root-store-context";
import { UserEmployee } from "../../../../api/base/ConfigApi";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Upload,
} from "antd";
import styles from "./notes.module.scss";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import {
  PaperClipOutlined,
  CloudUploadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

export type NoteType = {
  writeAccess: boolean;
  accountNumber: string;
  NoteID: string;
  opened: any;
  openedStr: string;
  openedBy: UserEmployee;
  openEmployeeKey: string;
  closed: any;
  closedStr: string;
  created: any;
  closedBy: UserEmployee;
  assignedTo: UserEmployee;
  closeEmployeeKey: string;
  category1: string;
  category2: string | null;
  noteText: string;
  resolution: string;
  Type: string;
  contact: string;
  fileName: string;
  fileUrl: string;
  onWeb: "Yes" | "No";
  responsibleEmployeeKey: string;
  binaryFile: any | null;
  isFile: boolean;
  isNew?: boolean;
  // Local-only note being created: exists in the UI but has NOT been POSTed
  // yet. It is persisted on Save and discarded on Cancel.
  isDraft?: boolean;
};

const Note = observer(
  (props: {
    item: NoteType;
    isDraft?: boolean;
    onCancel?: () => void;
    onSaved?: () => void;
  }): ReactElement => {
    const { isDraft, onCancel, onSaved } = props;
    const { ricStore, applicationStore } = useStore();
    const [form] = Form.useForm();
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [isUpload, setIsUpload] = useState<boolean>(false);
    const [note, setNote] = useState<NoteType>(props.item);
    // True once a field is edited; back to false after a successful save. Drives
    // the Save button visibility (only shown when there is something to save).
    const [dirty, setDirty] = useState<boolean>(false);
    const fileInput = useRef(null);

    useEffect(() => {
      if (dirty) return;
      setNote(props.item);
      form.setFieldsValue(props.item);
    }, [props.item, dirty]);

    const changeIsUpdating = (status: boolean) => {
      setIsUpdating(status);
    };
    const changeIsUploading = (status: boolean) => {
      setIsUpload(status);
    };
    const getDataStr = (date: dayjs.Dayjs): string => {
      if (date)
        return `${date.year()}-${
          date.month() < 9 ? `0${date.month() + 1}` : date.month() + 1
        }-${date.date()}`;

      return "";
    };

    // Common-code lists can contain duplicates and trailing spaces; the Select
    // keys options by value, so duplicates break its virtual list on scroll.
    const toOptions = (items: string[]) =>
      [...new Set((items || []).map((el) => el?.trim()).filter(Boolean))].map(
        (el) => ({ value: el, label: el })
      );

    const handleCancel = () => {
      if (isDraft) {
        onCancel?.();
        return;
      }
      form.resetFields();
      setNote(props.item);
      setDirty(false);
    };

    return (
      <Spin spinning={isUpdating}>
        <Form
          className={styles.noteItem}
          form={form}
          variant="outlined"
          layout="vertical"
          initialValues={note}
          labelCol={{ span: 24 }}
          style={{ width: "100%" }}
          onFinish={() => {
            const values = form.getFieldsValue();
            if (isDraft) {
              ricStore
                .createNoteWithData(
                  {
                    ...values,
                    binaryFile: note.binaryFile,
                    fileName: note.fileName,
                  },
                  changeIsUpdating
                )
                .then((ok) => {
                  if (!ok) return;
                  setDirty(false);
                  onSaved?.();
                })
                .catch(console.log);
              return;
            }
            ricStore
              .updateNote(values, changeIsUpdating)
              .then(() => setDirty(false))
              .catch(console.log);
          }}
          onValuesChange={(changedValues, allValues) => {
            setNote((prev) => ({ ...prev, ...allValues }));
            setDirty(true);
          }}
          // validateMessages={validateMessages}
          // requiredMark={customizeRequiredMark}
          autoComplete="off"
          scrollToFirstError={{ block: "center", inline: "nearest" }}
        >
          <Row>
            <Col span={24}>
              <Form.Item<NoteType> name="closeEmployeeKey" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="responsibleEmployeeKey" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="contact" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="created" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="fileName" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="onWeb" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="openEmployeeKey" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="Type" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="accountNumber" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="NoteID" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="resolution" hidden>
                <Input />
              </Form.Item>
              <Form.Item<NoteType> name="binaryFile" hidden>
                <Input type="file" ref={fileInput} value={note.binaryFile} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item<NoteType> name="openedStr" label="Opened">
                <Input disabled={true} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item<NoteType> label="Opened By">
                <Input disabled={true} value={note?.openedBy?.employeeName} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item<NoteType> name="closed" label="Closed">
                <DatePicker
                  style={{ width: "100%" }}
                  format={"MM/DD/YYYY"}
                  disabled={!ricStore.isEditable}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item<NoteType> label="Closed By">
                <Input disabled={true} value={note?.closedBy?.employeeName} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item<NoteType>
                name="Type"
                label="Type"
                getValueProps={(value) => ({ value: value?.trim() || undefined })}
              >
                <Select
                  data-testid="ric-note-type-select"
                  options={toOptions(ricStore.typeItems)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={18}>
              <Form.Item<NoteType>
                name="category1"
                label="Category 1"
                getValueProps={(value) => ({ value: value || undefined })}
              >
                <Select
                  data-testid="ric-note-category1-select"
                  placeholder="Select category..."
                  options={toOptions(ricStore.category1Items)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={16} lg={20}>
              <Form.Item<NoteType>
                name="noteText"
                label="Note"
                rules={[
                  { required: true, whitespace: true, message: "Note is required." },
                ]}
              >
                <TextArea rows={3} className={styles.whiteField} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={4}>
              <Form.Item
                className={styles.uploadField}
                label={<span style={{ visibility: "hidden" }}>Upload</span>}
              >
                <Space wrap>
                  <Upload
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={(info) => {
                      const file = info.file.originFileObj ?? info.file;
                      if (isDraft) {
                        setNote((prev) => ({
                          ...prev,
                          binaryFile: file,
                          fileName: info.file.name,
                          fileUrl: "",
                        }));
                        form.setFieldsValue({ fileName: info.file.name });
                        setDirty(true);
                        return;
                      }
                      if (!ricStore.isUploadFile) {
                        setIsUpload(true);
                        const newNote = {
                          ...note,
                          binaryFile: file,
                          fileName: info.file.name,
                        };
                        setNote(newNote);
                        ricStore
                          .updateFileNote(newNote, changeIsUploading)
                          .then((fileUrl) => {
                            setNote((prev) => {
                              return {
                                ...prev,
                                fileUrl: fileUrl,
                              };
                            });
                          })
                          .catch(console.log);
                      }
                    }}
                    listType="text"
                    onPreview={() => {
                      <></>;
                    }}
                  >
                    <Button
                      data-testid="ric-note-upload-btn"
                      icon={
                        isUpload ? <LoadingOutlined /> : <CloudUploadOutlined />
                      }
                    >
                      Upload
                    </Button>
                  </Upload>
                  {!isUpload && note.fileName.length ? (
                    <Button
                      data-testid="ric-note-view-file-btn"
                      icon={<PaperClipOutlined />}
                      onClick={() => {
                        if (!note.fileUrl && note.binaryFile instanceof Blob) {
                          window.open(
                            URL.createObjectURL(note.binaryFile),
                            "_blank"
                          );
                          return;
                        }
                        window.open(note.fileUrl, "_blank");
                      }}
                    >
                      View
                    </Button>
                  ) : (
                    <></>
                  )}
                </Space>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item<NoteType> name="resolution" label="Resolution">
                <Input
                  className={styles.whiteField}
                  disabled={!ricStore.isEditable}
                />
              </Form.Item>
            </Col>
          </Row>
          {ricStore.isEditable && (dirty || isDraft) && (
            <Row justify="end">
              <Col>
                <Space>
                  <Button
                    data-testid="ric-note-cancel-btn"
                    className="ric-pill-btn"
                    onClick={handleCancel}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="ric-note-save-btn"
                    className="ric-primary-btn"
                    type="primary"
                    htmlType="submit"
                    loading={isUpdating}
                  >
                    Save
                  </Button>
                </Space>
              </Col>
            </Row>
          )}
        </Form>
      </Spin>
    );
  }
);
export default Note;
